import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useTheme } from '@/contexts/ThemeContext';
import { Users, Bell, TrendingUp, Activity, DollarSign, Building2, Loader2 } from 'lucide-react';
import { apiService } from '@/services/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface DashboardStats {
  overview: {
    totalUsers: number;
    totalEstablishments: number;
    totalJobs: number;
    totalApplications: number;
    totalRevenue: number;
    activeUsers: number;
    userGrowth: number;
    revenueGrowth: number;
    jobGrowth: number;
  };
  recentActivity: {
    recentUsers: Array<{
      id: string;
      name: string;
      email: string;
      createdAt: string;
      userType: number;
    }>;
  };
  charts: {
    monthlyUsers: Array<{
      month: string;
      users: number;
    }>;
  };
  support: {
    total: number;
    open: number;
    inProgress: number;
    resolved: number;
    closed: number;
  };
  subscriptions: {
    total: number;
    active: number;
    cancelled: number;
    revenue: number;
  };
  activities: Array<{
    id: string;
    type: string;
    description: string;
    timestamp: string;
  }>;
}

export function Dashboard() {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await apiService.getDashboardStats();
        
        if (response.type === 0 || response.type === 1) { // Backend returns type: 1
          setStats(response.data);
        } else {
          toast.error(response.message || 'Failed to load dashboard data');
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    void fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Failed to load dashboard data</p>
      </div>
    );
  }

  const statsCards = [
    {
      title: 'Total Users',
      value: stats.overview.totalUsers.toLocaleString(),
      change: `+${stats.overview.userGrowth}%`,
      changeType: 'positive' as const,
      icon: Users,
      description: `${stats.overview.activeUsers.toLocaleString()} active users`,
    },
    {
      title: 'Establishments',
      value: stats.overview.totalEstablishments.toLocaleString(),
      change: '+5%',
      changeType: 'positive' as const,
      icon: Building2,
      description: 'Registered establishments',
    },
    {
      title: 'Total Jobs',
      value: stats.overview.totalJobs.toLocaleString(),
      change: `+${stats.overview.jobGrowth}%`,
      changeType: 'positive' as const,
      icon: Activity,
      description: 'Active job postings',
    },
    {
      title: 'Revenue',
      value: `$${stats.overview.totalRevenue.toLocaleString()}`,
      change: `+${stats.overview.revenueGrowth}%`,
      changeType: 'positive' as const,
      icon: DollarSign,
      description: 'Total platform revenue',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">
          Welcome to SnapShift Admin Panel. Here's an overview of your platform.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statsCards.map((card) => (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
              <card.icon
                className="h-4 w-4 text-muted-foreground"
                style={{ color: currentTheme.colors.primary }}
              />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{card.value}</div>
              <p className="text-xs text-muted-foreground">{card.description}</p>
              <div className="flex items-center pt-2">
                <TrendingUp
                  className={`h-4 w-4 ${
                    card.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                  }`}
                />
                <span
                  className={`text-xs font-medium ml-1 ${
                    card.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {card.change}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
        <Card className="col-span-4">
          <CardHeader>
            <CardTitle>User Growth</CardTitle>
            <CardDescription>
              Monthly user registration trends for {new Date().getFullYear()}
            </CardDescription>
          </CardHeader>
          <CardContent className="pl-2">
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.charts.monthlyUsers}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value.substring(0, 3)}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                  />
                  <Bar 
                    dataKey="users" 
                    fill={currentTheme.colors.primary} 
                    radius={[8, 8, 0, 0]}
                    name="New Users"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="col-span-3">
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>
              Latest platform activities
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats.recentActivity.recentUsers.length > 0 ? (
                stats.recentActivity.recentUsers.map((user) => (
                  <div key={user.id} className="flex items-center space-x-4">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: currentTheme.colors.primary }}
                    />
                    <div className="flex-1 space-y-1">
                      <p className="text-sm font-medium leading-none">New user registered</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {format(new Date(user.createdAt), 'MMM dd, HH:mm')}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No recent activity
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid gap-4 ">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-row justify-between gap-2">
            <button
              className="w-full text-left p-3 rounded-lg border hover:bg-accent transition-colors"
              style={{ borderColor: currentTheme.colors.primary + '20' }}
              onClick={() => navigate('/notifications')}
            >
              <div className="flex items-center space-x-2">
                <Bell className="h-4 w-4" style={{ color: currentTheme.colors.primary }} />
                <span>Send Notification</span>
              </div>
            </button>
            <button
              className="w-full text-left p-3 rounded-lg border hover:bg-accent transition-colors"
              style={{ borderColor: currentTheme.colors.primary + '20' }}
              onClick={() => navigate('/users')}
            >
              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4" style={{ color: currentTheme.colors.primary }} />
                <span>View Users</span>
              </div>
            </button>
            <button
              className="w-full text-left p-3 rounded-lg border hover:bg-accent transition-colors"
              style={{ borderColor: currentTheme.colors.primary + '20' }}
              onClick={() => navigate('/establishments')}
            >
              <div className="flex items-center space-x-2">
                <Building2 className="h-4 w-4" style={{ color: currentTheme.colors.primary }} />
                <span>View Establishments</span>
              </div>
            </button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
